// File: MetricGroupSetup.tsx
import React, { useEffect, useState } from "react";
import MetricGroupTable from "../MetricGroup-Table/MetricGroup-Table";
import MetricGroupForm from "../MetricGroup-Form/MetricGroup-Form";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import Dialog from "../FormDialog/Dialog";
import { getUserInfo } from "../../../services/authService-sighup";
import apiClientTimeseries from "../../../services/apiClientTimeseries";
import { getHeaders as getMetricHeaders } from "../../ApiHeaders/apiHeadersTimeseries";
import LoadingStyle from "../../Loading/Loading";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";

interface MetricGroupSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
}


const MetricGroupSetup: React.FC<MetricGroupSetupProps> = ({
  registerTabSwitchGuard,
  canDelete = false
}) => {
  const [metricGroups, setMetricGroups] = useState<any[]>([]);
  const [selectedMetricGroup, setSelectedMetricGroup] = useState<any | null>(
    null
  );
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );

  const [user, setUser] = useState<{ fullName: string; email: string } | null>(
    null
  );

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);

  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>(
    {
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    }
  );

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };

  // ------------------ FETCH USER INFO ------------------
  useEffect(() => {
    const info = getUserInfo();
    if (info) {
      setUser({
        fullName: info.fullName || "Unknown User",
        email: info.email || "",
      });
    }
  }, []);

  const createdBy = user ? `${user.email || user.fullName}` : "Unknown User";
  const modifiedBy = user ? `${user.email || user.fullName}` : "Unknown User";

  // ------------------ TAB SWITCH GUARD ------------------
  useEffect(() => {
    if (registerTabSwitchGuard) {
      registerTabSwitchGuard(() => {
        return new Promise<boolean>((resolve) => {
          if (isFormDirty || isCreateMode) {
            setDialog({
              open: true,
              title: "Unsaved Changes",
              message:
                "You have unsaved changes. Are you sure you want to discard them?",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              onConfirm: () => {
                setIsFormDirty(false);
                setIsCreateMode(false);
                closeDialog();
                resolve(true);
              },
              onCancel: () => {
                closeDialog();
                resolve(false);
              },
            });
          } else {
            resolve(true);
          }
        });
      });
    }
  }, [isFormDirty, isCreateMode]);

  // ------------------ API ERROR HANDLER ------------------
  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;
    const errorResponse = err.response?.data?.error;
    const backendMessage =
      errorResponse?.error?.message || errorResponse?.message;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = backendMessage || "The submitted data is invalid.";
        break;
      case 401:
        title = "Access Denied";
        details = "You're not authorized to access this content.";
        break;
      case 403:
        title = "Access Denied";
        details = "You need permission to access this site.";
        break;
      case 404:
        title = "Not Found";
        details = backendMessage || "The requested resource was not found.";
        break;
      case 500:
        title = "Internal Server Error";
        details =
          backendMessage || "Server error occurred. Please try again later.";
        break;
      default:
        if (
          err.message?.includes("Network Error") ||
          err.message?.includes("timeout")
        ) {
          title = "Network Error";
          details = "Unable to connect to the server.";
        } else {
          title = fallbackTitle;
          details = backendMessage || err.message || "Unexpected error.";
        }
        break;
    }

    setError({ title, details });
  };

  // ------------------ FETCH METRIC GROUPS ------------------
  const fetchMetricGroups = async () => {
    setLoading(true);
    try {
      const headers = await getMetricHeaders();

      const response = await apiClientTimeseries.get(`/metric-group/`, {
        headers,
      });

      const data = response?.data?.response?.data || [];

      setMetricGroups(data);
    } catch (err) {
      handleApiError(err, "Failed to Fetch Metric Groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetricGroups();
  }, []);

  // ------------------ CREATE METRIC GROUP ------------------
  const handleCreateMetricGroup = async (newMetricGroup: any) => {
    try {
      const headers = await getMetricHeaders();

      const payload = {
        metric_group_name: newMetricGroup.metric_group_name,
        iso_code: newMetricGroup.iso_code,
        schedule_time: new Date().toISOString(),
        sys_created_by: createdBy,
        sys_modified_by: modifiedBy,
      };

      await apiClientTimeseries.post(`/metric-group/`, payload, { headers });

      await fetchMetricGroups();

      setDialog({
        open: true,
        title: "Success",
        message: `Metric Group "${newMetricGroup.metric_group_name}" created successfully.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } catch (err) {
      handleApiError(err, "Failed to Create Metric Group");
    }
  };

  // ------------------ UPDATE METRIC GROUP ------------------
  const handleSaveMetricGroup = async (
    metricGroupId: number,
    updatedMetricGroup: any
  ) => {
    try {
      const headers = await getMetricHeaders();

      const payload = {
        metric_group_id: metricGroupId,
        metric_group_name: updatedMetricGroup.metric_group_name,
        iso_code: updatedMetricGroup.iso_code,
        sys_modified_by: modifiedBy,
        metric_ids: updatedMetricGroup.metric_ids,
      };

      const response = await apiClientTimeseries.put(`/metric-group/`, payload, {
        headers,
      });

      if (response.status === 200) {
        await fetchMetricGroups();

        setDialog({
          open: true,
          title: "Success",
          message: `Metric Group "${updatedMetricGroup.metric_group_name}" updated successfully.`,
          confirmButtonText: "Close",
          onConfirm: closeDialog,
        });
      }
    } catch (err) {
      handleApiError(err, "Failed to Update Metric Group");
    }
  };

  // ------------------ DELETE METRIC GROUP ------------------
  // const handleDeleteMetricGroupRequest = async (metricGroupId: number) => {
  //   try {
  //     const group = metricGroups.find((g) => g.metric_group_id === metricGroupId);
  //     if (!group) {
  //       setDialog({
  //         open: true,
  //         title: "Not Found",
  //         message: "Metric group not found.",
  //         confirmButtonText: "Close",
  //         onConfirm: closeDialog,
  //       });
  //       return;
  //     }

  //     const hasMetrics = Array.isArray(group.metric_ids) && group.metric_ids.length > 0;
  //     if (hasMetrics) {
  //       setDialog({
  //         open: true,
  //         title: "Cannot Delete",
  //         message: "Metric group has metrics associated with it and cannot be deleted.",
  //         confirmButtonText: "Close",
  //         onConfirm: closeDialog,
  //       });
  //       return;
  //     }

  //     // Confirm delete
  //     setDialog({
  //       open: true,
  //       title: "Confirm Delete",
  //       message: "Are you sure you want to delete this metric group?",
  //       confirmButtonText: "Yes",
  //       cancelButtonText: "No",
  //       onConfirm: async () => {
  //         try {
  //           const headers = await getMetricHeaders();
  //           await apiClientTimeseries.delete(`/metric-group/${metricGroupId}`, { headers });
  //           await fetchMetricGroups();
  //           setSelectedMetricGroup(null);
  //           setSelectedRow(null);
  //           setDialog({
  //             open: true,
  //             title: "Deleted",
  //             message: "Metric group deleted successfully.",
  //             confirmButtonText: "Close",
  //             onConfirm: closeDialog,
  //           });
  //         } catch (err) {
  //           handleApiError(err, "Failed to Delete Metric Group");
  //         }
  //       },
  //       onCancel: closeDialog,
  //     });
  //   } catch (err) {
  //     handleApiError(err, "Failed to Delete Metric Group");
  //   }
  // };
  const handleDeleteMetricGroupRequest = async (metricGroupId: number) => {
    try {
      const headers = await getMetricHeaders();
  
      const group = metricGroups.find(
        (g) => g.metric_group_id === metricGroupId
      );
  
      if (!group) {
        setDialog({
          open: true,
          title: "Not Found",
          message: "Metric group not found.",
          confirmButtonText: "Close",
          onConfirm: closeDialog,
        });
        return;
      }
  
      const hasMetrics =
        Array.isArray(group.metric_ids) && group.metric_ids.length > 0;
  
      // STEP 1: Remove metrics if present
      if (hasMetrics) {
        const removeMetricsPayload = {
          metric_group_id: metricGroupId,
          metric_group_name: group.metric_group_name,
          iso_code: group.iso_code,
          metric_ids: [], // 👈 REMOVE ALL METRICS
          sys_modified_by: modifiedBy,
        };
  
        await apiClientTimeseries.put(
          `/metric-group/`,
          removeMetricsPayload,
          { headers }
        );
      }
  
      // STEP 2: Delete metric group
      await apiClientTimeseries.delete(
        `/metric-group/${metricGroupId}`,
        { headers }
      );
  
      await fetchMetricGroups();
  
      setSelectedMetricGroup(null);
      setSelectedRow(null);
      setIsFormDirty(false);
      setIsCreateMode(false);
  
      setDialog({
        open: true,
        title: "Deleted",
        message: hasMetrics
          ? "Metric group and its associated metrics were deleted successfully."
          : "Metric group deleted successfully.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } catch (err) {
      handleApiError(err, "Failed to Delete Metric Group");
    }
  };
  

  // ------------------ ROW SELECT ------------------
  const handleRowSelect = (metricGroup: any) => {
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedMetricGroup(metricGroup);
          setSelectedRow(metricGroup.metric_group_id);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedMetricGroup(metricGroup);
      setSelectedRow(metricGroup.metric_group_id);
    }
  };

  // ------------------ CLEAR SELECTION ------------------
  const handleClearSelection = () => {
    if (isFormDirty) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setSelectedRow(null);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedRow(null);
    }
  };

  // ------------------ JSX ------------------
  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>

      {/* TABLE */}
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <MetricGroupTable
          metricGroups={metricGroups}
          onRowSelect={handleRowSelect}
          onClearSelection={handleClearSelection}
          selectedRow={selectedRow}
        />
      </div>

      {/* FORM */}
      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
        <MetricGroupForm
          onCreateMetricGroup={handleCreateMetricGroup}
          selectedMetricGroup={selectedMetricGroup}
          onSaveChanges={handleSaveMetricGroup}
          onClearSelection={handleClearSelection}
          metricGroups={metricGroups}
          onDirtyChange={setIsFormDirty}
          onModeChange={(mode) => setIsCreateMode(mode === "create")}
          onDeleteMetricGroup={handleDeleteMetricGroupRequest}
          canDelete={canDelete}
        />
      </div>

      {/* DIALOGS */}
      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          onClose={closeDialog}
        />
      )}

      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default MetricGroupSetup;